/* ** *** *** ***
Covetrus PCI/Security Notice:
This file has been identified as potentially impacting user and/or data security.
All modifications to these files must be reviewed and merged by a designated Security Champion for this application.
Additional information, including specifics for Pull Requests, can be found here:
https://dvmwiki.atlassian.net/wiki/spaces/MCT/pages/507511182/Code+Reviews
*** *** *** ** */

import React from 'react';
import { PropTypes } from 'prop-types';
import { Link } from 'react-router-dom';
import ShippingAddressForm from '../../components/Forms/ShippingAddressForm';
import CreditCardInfoForm from '../../components/Forms/CreditCardInfoForm';
import Button from '../../components/Button';
import CancelButton from './CancelButton';
import styles from './ModalsLayout.css';
import CardList from './CardList';
import CardListPlusButton from '../../assets/CardListPlusButton';
import CommunicationHandler from '../../communicationHandler';

const handleUpdateDefaultPayment = (
  updateDefaultPayment,
  selectedCard,
  cardsArray,
  disableCreditCardList
) => () => {
  const selected = cardsArray.find(
    card =>
      (card.paymentProfileId ? card.paymentProfileId.toString() : '') ===
      selectedCard
  );
  updateDefaultPayment(selected);
  disableCreditCardList();
};

const handleKeyDown = (enableCreditCardInfoModal, e) => {
  if (e.keyCode === 13) {
    enableCreditCardInfoModal();
  }
};

const Modal = ({ children, profileId, token }) => (
  <div className={styles.modalShade}>
    <div className={styles.modal}>
      {children}
      <iframe
        id="add_payment"
        className="embed-responsive-item panel"
        name="add_payment"
        width="375px"
        height="900px"
        frameBorder="0"
        hidden
        scrolling="no"
        title="add_payment"
      />
    </div>
    {profileId && token ? (
      <CreditCardInfoForm hidden profileId={profileId} token={token} />
    ) : null}
  </div>
);

Modal.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.element),
    PropTypes.element,
  ]).isRequired,
  profileId: PropTypes.string,
  token: PropTypes.string,
};

const ModalsLayout = ({
  shippingAddressEnabled,
  creditCardInfoEnabled,
  prepopShippingInfo,
  prepopPaymentInfo,
  states,
  disableShippingAddressModal,
  disableCreditCardInfoModal,
  enableCreditCardInfoModal,
  enableCreditCardList,
  updateUserShippingInfo,
  updateUserPaymentInfo,
  updatePaymentOptions,
  distinctBillingAddress,
  creditCardListEnabled,
  disableCreditCardList,
  cardsArray,
  selectedCard,
  updateDefaultPayment,
  authNetHostedToken,
  paymentOptions,
  customer,
}) => {
  if (shippingAddressEnabled) {
    return (
      <Modal>
        <button className={styles.cancel} onClick={disableShippingAddressModal}>
          X
        </button>
        <ShippingAddressForm
          states={states}
          onSubmit={values => {
            updateUserShippingInfo(values);
            disableShippingAddressModal();
          }}
          initialValues={prepopShippingInfo}
        />
        <CancelButton onCancel={disableShippingAddressModal} />
      </Modal>
    );
  } else if (creditCardListEnabled) {
    return (
      <Modal>
        <button className={styles.cancel} onClick={disableCreditCardList}>
          X
        </button>
        <CardList
          paymentOptions={cardsArray}
          selectedCard={selectedCard}
          token={authNetHostedToken}
          profileId={customer.authnetClientProfileId}
        />
        {cardsArray && cardsArray.length >= 10 ? (
          <span className={styles.maxCards}>
            Maximum cards on file. Please{' '}
            <Link to="/contact" onClick={disableCreditCardList}>
              contact Customer Service
            </Link>.
          </span>
        ) : (
          <div
            onClick={enableCreditCardInfoModal}
            onKeyDown={e => handleKeyDown(enableCreditCardInfoModal, e)}
            className={styles.cardListAddBtnContainer}
            role="button"
            tabIndex="0"
          >
            <CardListPlusButton />
            <span>Add new card</span>
          </div>
        )}
        <Button
          type="button"
          className={styles.button}
          btnText="Save"
          disabled={!selectedCard}
          onClick={handleUpdateDefaultPayment(
            updateDefaultPayment,
            selectedCard,
            cardsArray,
            disableCreditCardList
          )}
        />
        <CancelButton onCancel={disableCreditCardList} />
      </Modal>
    );
  } else if (creditCardInfoEnabled && cardsArray.length > 0) {
    return (
      <Modal>
        <CommunicationHandler
          customer={customer}
          paymentOptions={paymentOptions}
          disableCreditCardInfoModal={disableCreditCardInfoModal}
          enableCreditCardList={enableCreditCardList}
          updatePaymentOptions={updatePaymentOptions}
        />
        <button className={styles.cancel} onClick={disableCreditCardInfoModal}>
          X
        </button>
        <CreditCardInfoForm
          states={states}
          onSubmit={values => {
            updatePaymentOptions(values);
            disableCreditCardInfoModal();
            enableCreditCardList();
          }}
          initialValues={prepopPaymentInfo}
          billingAddressEnabled={distinctBillingAddress}
          currentCards={cardsArray}
          token={authNetHostedToken}
          profileId={customer.authnetClientProfileId}
          hidden={false}
        />
      </Modal>
    );
  } else if (creditCardInfoEnabled) {
    return (
      <Modal
        token={authNetHostedToken}
        profileId={customer.authnetClientProfileId}
      >
        <CommunicationHandler
          customer={customer}
          paymentOptions={paymentOptions}
          disableCreditCardInfoModal={disableCreditCardInfoModal}
          updatePaymentOptions={updatePaymentOptions}
        />
        <button className={styles.cancel} onClick={disableCreditCardInfoModal}>
          X
        </button>
        <CreditCardInfoForm
          states={states}
          onSubmit={values => {
            updateUserPaymentInfo(values);
            updatePaymentOptions(values);
            disableCreditCardInfoModal();
            enableCreditCardList();
          }}
          initialValues={prepopPaymentInfo}
          billingAddressEnabled={distinctBillingAddress}
          currentCards={[]}
          token={authNetHostedToken}
          profileId={customer.authnetClientProfileId}
          hidden={false}
        />
      </Modal>
    );
  }

  return null;
};

ModalsLayout.propTypes = {
  shippingAddressEnabled: PropTypes.bool,
  creditCardInfoEnabled: PropTypes.bool,
  prepopShippingInfo: PropTypes.object,
  prepopPaymentInfo: PropTypes.object,
  states: PropTypes.array,
  updateUserShippingInfo: PropTypes.func.isRequired,
  updateUserPaymentInfo: PropTypes.func.isRequired,
  updatePaymentOptions: PropTypes.func,
  updateDefaultPayment: PropTypes.func,
  disableShippingAddressModal: PropTypes.func.isRequired,
  disableCreditCardInfoModal: PropTypes.func.isRequired,
  distinctBillingAddress: PropTypes.bool.isRequired,
  enableCreditCardList: PropTypes.func,
  creditCardListEnabled: PropTypes.bool,
  disableCreditCardList: PropTypes.func,
  cardsArray: PropTypes.array,
  selectedCard: PropTypes.string,
  enableCreditCardInfoModal: PropTypes.func,
  authNetHostedToken: PropTypes.string,
  paymentOptions: PropTypes.array,
  customer: PropTypes.object,
};
export default ModalsLayout;



// WEBPACK FOOTER //
// ./src/containers/Modals/ModalsLayout.jsx